import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Partners = () => {
  const partners = [
    { name: 'AWS', logo: '/assets/aws-logo.png' },
    { name: 'Vodafone', logo: '/assets/vodafone-logo.png' },
    { name: 'O2', logo: '/assets/o2-logo.png' },
    { name: 'EE', logo: '/assets/ee-logo.png' },
    { name: 'BT', logo: '/assets/bt-logo.png' },
    { name: 'Gamma Horizon', logo: '/assets/gamma-horizon-logo.png' },
    { name: 'Microsoft', logo: '/assets/microsoft-logo.png' },
    { name: 'Dell', logo: '/assets/dell-logo.jpg' },
    { name: 'HP', logo: '/assets/hp-logo.png' },
    { name: 'Huntress', logo: '/assets/huntress-logo.png' }
  ];

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = direction === 'left' ? -280 : 280;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  // Duplicate for infinite feel
  const items = [...partners, ...partners];

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-slate-800 mb-10">
          Trusted Partners
        </h2>
        <div className="relative group">
          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}

          {/* Carousel track */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 py-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {items.map((partner, i) => (
              <div
                key={`${partner.name}-${i}`}
                data-testid={`partner-card-${partner.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex-shrink-0 w-[180px] flex flex-col items-center justify-center p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="h-16 flex items-center justify-center mb-2">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-14 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const textSpan = document.createElement('div');
                      textSpan.className = 'text-xl font-bold text-slate-700';
                      textSpan.textContent = partner.name;
                      e.target.parentElement.appendChild(textSpan);
                    }}
                  />
                </div>
                <p className="text-xs font-medium text-slate-600 text-center">{partner.name}</p>
              </div>
            ))}
          </div>

          {/* Right arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          )}

          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-slate-50 to-transparent pointer-events-none z-[5]"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none z-[5]"></div>
        </div>
      </div>
    </section>
  );
};

export default Partners;
